import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import {
  type ExpenseBudgetItem,
  type ExpenseBudgetItemExpenseStats,
  type ExpenseBudgetItemStats,
  ExpenseBudgetItemType,
  type ExpenseStructuredBudgetItem,
  type ExternalLink,
  type Maybe,
  type Order,
  type OurWorkBudgetItem,
  type OurWorkBudgetItemExpenseStats,
  type OurWorkBudgetItemStats,
  type OurWorkOverviewBudgetItem,
  OurWorkOverviewForProjectDocument,
  type OurWorkOverviewForProjectQuery,
  type OurWorkOverviewForProjectQueryVariables,
  type Project,
  type ProjectDetailQuery,
  type ProjectExpenseStats,
  type ProjectOurWorkStats,
  type ProjectStats,
  ProjectTypeEnum,
  UpdateOurWorkBudgetItemsDocument,
  type UpdateOurWorkBudgetItemsMutation,
  type UpdateOurWorkBudgetItemsMutationVariables,
  type User,
  type UserProfile,
  type WorkInProgressItemStat,
} from "@src/__generated__/graphql";
import { client } from "@src/services/client";
import { appStore } from "@src/stores/AppStore";
import { toApiDate } from "@src/utils/dates";
import type { CalendarValues } from "@uselessdev/datepicker";
import { isDate } from "date-fns";
import { filter, map, sortBy } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import type { DeepPick } from "ts-essentials";

type TProject = NonNullable<ProjectDetailQuery["project"]>;
type TProjectInvoiceStat = NonNullable<TProject["invoiceStats"]>;
type TProjectPrepaymentStats = NonNullable<TProject["prepaymentStats"]>;

type TExpenseStructured = NonNullable<TProject["expensesStructured"]>[0];

type TOurWorkOverview = NonNullable<
  OurWorkOverviewForProjectQuery["project"]
>["ourWorkOverview"][0];
type TOurWorkBudgetItem = NonNullable<TOurWorkOverview["budgetItems"]>[0];

type TOtherProjectManager = NonNullable<TProject["otherProjectManagers"]>[0];
type TProjectManager = NonNullable<TProject["projectManager"]>;
type TContact = TProject["contact"];
type TFinishedBy = TProject["finishedBy"];
type TArchivedBy = TProject["archivedBy"];
type TBrand = NonNullable<TProject["brand"]>;

export class OurWorkBudgetItemExpenseStatsModel {
  id: OurWorkBudgetItemExpenseStats["id"];
  expense: Partial<OurWorkBudgetItemExpenseStats["expense"]>;
  tracked_time?: OurWorkBudgetItemExpenseStats["tracked_time"];
  position?: Partial<OurWorkBudgetItemExpenseStats["position"]>;
  report_date?: OurWorkBudgetItemExpenseStats["report_date"];
  amount: OurWorkBudgetItemExpenseStats["amount"];
  task?: Maybe<{
    id: NonNullable<OurWorkBudgetItemExpenseStats["task"]>["id"];
    name: NonNullable<OurWorkBudgetItemExpenseStats["task"]>["name"];
  }>;

  constructor(src: {
    id: OurWorkBudgetItemExpenseStats["id"];
    expense: Partial<OurWorkBudgetItemExpenseStats["expense"]>;
    tracked_time?: OurWorkBudgetItemExpenseStats["tracked_time"];
    position?: Partial<OurWorkBudgetItemExpenseStats["position"]>;
    report_date?: OurWorkBudgetItemExpenseStats["report_date"];
    amount: OurWorkBudgetItemExpenseStats["amount"];
    task?: Maybe<{
      id: NonNullable<OurWorkBudgetItemExpenseStats["task"]>["id"];
      name: NonNullable<OurWorkBudgetItemExpenseStats["task"]>["name"];
    }>;
  }) {
    this.id = src.id;
    this.expense = src.expense;
    this.tracked_time = src.tracked_time;
    this.position = src.position;
    this.report_date = src.report_date;
    this.amount = src.amount;
    this.task = src.task;
  }
}

export class OurWorkBudgetItemModel {
  readonly __typename?: string;
  id: OurWorkBudgetItem["id"];
  item_code: OurWorkBudgetItem["item_code"];
  title: OurWorkBudgetItem["title"];
  completed_at: OurWorkBudgetItem["completed_at"];
  requested_for_deletion_at: OurWorkBudgetItem["requested_for_deletion_at"];
  @observable.ref hasPlannedExpenses: boolean;
  @observable hidden: OurWorkBudgetItem["hidden"];
  @observable order: OurWorkBudgetItem["order"];
  @observable deadline: CalendarValues;
  @observable note: OurWorkBudgetItem["note"];

  @observable
  lastWorkInProgressItemStat: TOurWorkBudgetItem["lastWorkInProgressItemStat"];
  expenseStats: OurWorkBudgetItemExpenseStatsModel[];

  @observable stats: {
    total_budget: OurWorkBudgetItemStats["total_budget"];
    total_time: OurWorkBudgetItemStats["total_time"];
    spent_budget: OurWorkBudgetItemStats["spent_budget"];
    spent_budget_percent: OurWorkBudgetItemStats["spent_budget_percent"];
    spent_time: OurWorkBudgetItemStats["spent_time"];
    remaining_time: OurWorkBudgetItemStats["remaining_time"];
    remaining_budget: OurWorkBudgetItemStats["remaining_budget"];
    credit: OurWorkBudgetItemStats["credit"];
    credit_balance: OurWorkBudgetItemStats["credit_balance"];
    invoiced_percent: OurWorkBudgetItemStats["invoiced_percent"];
    invoiceable: OurWorkBudgetItemStats["invoiceable"];
    uninvoiced: OurWorkBudgetItemStats["uninvoiced"];
    net_profit: OurWorkBudgetItemStats["net_profit"];
    net_margin_percent: OurWorkBudgetItemStats["net_margin_percent"];
    efficiency_percent: OurWorkBudgetItemStats["efficiency_percent"];
  };

  @observable tasks: TOurWorkBudgetItem["tasks"];
  @observable doneTaskCount: number;
  @observable taskCount: number;
  @observable showDoneTasks = false;
  @observable doneTasksLoaded = false;
  @observable isDoneTasksLoading = false;

  constructor(src: TOurWorkOverview["budgetItems"][0]) {
    makeObservable(this);
    this.__typename = src.__typename;
    this.id = src.id;
    this.item_code = src.item_code;
    this.title = src.title;
    this.completed_at = src.completed_at ? src.completed_at : undefined;
    this.requested_for_deletion_at = src.requested_for_deletion_at
      ? src.requested_for_deletion_at
      : undefined;
    this.hasPlannedExpenses = src.plannedExpenses.length > 0;
    this.lastWorkInProgressItemStat = src.lastWorkInProgressItemStat;
    this.stats = src.stats;
    this.tasks = src.tasks;
    this.doneTaskCount = src.done_task_count;
    this.taskCount = src.task_count;
    this.hidden = src.hidden;
    this.order = src.order;
    this.deadline = {
      start: src?.deadline ? new Date(src.deadline) : undefined,
      end: src?.deadline ? new Date(src.deadline) : undefined,
    };
    this.note = src?.note;

    if ("expenseStats" in src) {
      this.expenseStats = src.expenseStats.map(
        (stat) =>
          new OurWorkBudgetItemExpenseStatsModel({
            ...stat,
            amount: stat.expense_item_amount,
            report_date: stat.report_date ?? undefined,
          }),
      );
    } else {
      this.expenseStats = [];
    }
  }

  @action.bound
  setProgressPercent(value: WorkInProgressItemStat["progress_percent"]) {
    if (this.lastWorkInProgressItemStat) {
      this.lastWorkInProgressItemStat.progress_percent = value;
    }
  }

  @action.bound
  addTask(task: OurWorkBudgetItemModel["tasks"][0]) {
    if (task.parent?.id) {
      for (const currentTask of this.tasks) {
        if (currentTask.id !== task.parent.id) continue;
        if (Array.isArray(currentTask.subtasks)) {
          currentTask.subtasks.push(task);
        } else {
          currentTask.subtasks = [task];
        }
      }
      return;
    }

    this.tasks = [task, ...this.tasks];
  }

  @action.bound updateTask(updatedTask: OurWorkBudgetItemModel["tasks"][0]) {
    this.tasks = this.tasks.map((task) => {
      if (task.id === updatedTask.id) return updatedTask;
      task.subtasks = task.subtasks?.map((subtask) => {
        return subtask.id === updatedTask.id ? updatedTask : subtask;
      });
      return task;
    });
  }

  @action.bound
  removeTask(id: OurWorkBudgetItemModel["tasks"][0]["id"]) {
    this.tasks = this.tasks.filter((t) => t.id !== id);
  }

  @action.bound toggleHidden(): void {
    this.hidden = !this.hidden;
  }

  @action.bound setOrder(newOrder: number): void {
    this.order = newOrder;
  }

  @action.bound setDeadline(deadline: CalendarValues): void {
    this.deadline = deadline;
  }

  @action.bound clearDeadline(): void {
    this.deadline = {
      start: undefined,
      end: undefined,
    };
  }

  @action.bound
  updateHasPlannedExpenses(value: boolean) {
    this.hasPlannedExpenses = value;
  }

  @action.bound
  setTasks(tasks: TOurWorkBudgetItem["tasks"]) {
    this.tasks = tasks;
  }

  @action.bound
  setShowDoneTasks(value: boolean) {
    this.showDoneTasks = value;
  }

  @action.bound
  setDoneTasksLoaded(value: boolean) {
    this.doneTasksLoaded = value;
  }

  @action.bound
  setIsDoneTasksLoading(value: boolean) {
    this.isDoneTasksLoading = value;
  }
}

export class OurWorkOverviewModel {
  id: OurWorkOverviewBudgetItem["id"];
  @observable budgetItems: OurWorkBudgetItemModel[];
  @observable responseBudgetItems: TOurWorkOverview["budgetItems"];
  name: OurWorkOverviewBudgetItem["name"];
  total_budget: OurWorkOverviewBudgetItem["total_budget"];
  credit: OurWorkOverviewBudgetItem["credit"];

  constructor(src: TOurWorkOverview) {
    makeObservable(this);
    this.id = src.id;
    this.name = src.name;
    this.total_budget = src.total_budget;
    if (src.budgetItems.every(({ order }) => order === 0)) {
      this.budgetItems = map(
        src.budgetItems.toSorted((a, b) =>
          Number(a.id) > Number(b.id) ? 1 : -1,
        ),
        (i) => new OurWorkBudgetItemModel(i),
      );
    } else {
      this.budgetItems = map(
        src.budgetItems.toSorted((a, b) => (a.order > b.order ? 1 : -1)),
        (i) => new OurWorkBudgetItemModel(i),
      );
    }
    this.responseBudgetItems = src.budgetItems;
    this.credit = src.credit;
  }

  updateTrackedTime(taskId: string, trackedTime: number | undefined) {
    const task = this.budgetItems
      .flatMap((i) => i.tasks)
      .find((i) => i.id === taskId);
    if (task?.stats) {
      task.stats.spent_time += trackedTime ?? 0;
      const taskBudgetItem = this.budgetItems.find(
        (bi) => bi.id === task.ourWorkBudgetItem?.id,
      );
      if (taskBudgetItem) {
        const timeInSeconds = (trackedTime ?? 0) / 60 / 60;
        const time =
          appStore.workspaceStore.settings?.time_tracking_rate_type ===
          "man-day"
            ? timeInSeconds / 8
            : timeInSeconds;
        taskBudgetItem.stats.spent_time += time;
      }
    }
  }

  @action.bound reorderBudgetItems(event: DragEndEvent): void {
    if (!event.active || !event.over || event.active.id === event.over.id) {
      return;
    }
    const movedItemIndex = this.budgetItems.findIndex(
      ({ id }) => event.active.id === id,
    );
    const droppedOnIndex = this.budgetItems.findIndex(
      ({ id }) => event.over?.id === id,
    );

    this.budgetItems = arrayMove(
      this.budgetItems,
      movedItemIndex,
      droppedOnIndex,
    );
    this.budgetItems.forEach((budgetItem, index) => {
      budgetItem.setOrder(index);
    });
  }

  @action.bound async updateBudgetItems() {
    const { error } = await client.mutation<
      UpdateOurWorkBudgetItemsMutation,
      UpdateOurWorkBudgetItemsMutationVariables
    >(UpdateOurWorkBudgetItemsDocument, {
      budgetItems: this.budgetItems.map((budgetItem) => ({
        id: budgetItem.id,
        hidden: budgetItem.hidden,
        deadline: isDate(budgetItem.deadline?.start)
          ? toApiDate(budgetItem.deadline.start)
          : null,
      })),
    });

    return error;
  }

  @action.bound restoreBudgetItems(): void {
    this.budgetItems = this.responseBudgetItems
      .sort((a, b) => (a.order > b.order ? 1 : -1))
      .map((budgetItem) => new OurWorkBudgetItemModel(budgetItem));
  }

  @action.bound addBudgetItem(budgetItem: TOurWorkOverview["budgetItems"][0]) {
    this.budgetItems.push(new OurWorkBudgetItemModel(budgetItem));
    this.budgetItems.sort((a, b) => (a.order > b.order ? 1 : -1));
  }
}

export class ExpenseBudgetItemModel {
  readonly __typename?: string;
  id: ExpenseBudgetItem["id"];
  item_code: ExpenseBudgetItem["item_code"];
  title: ExpenseBudgetItem["title"];
  type: ExpenseBudgetItem["type"];
  @observable.ref completed_at: ExpenseBudgetItem["completed_at"];
  @observable.ref hasPlannedExpenses: boolean;

  requested_for_deletion_at: ExpenseBudgetItem["requested_for_deletion_at"];

  @observable stats: {
    total_budget: ExpenseBudgetItemStats["total_budget"];
    real_expenses: ExpenseBudgetItemStats["real_expenses"];
    real_commission: ExpenseBudgetItemStats["real_commission"];
    real_commission_relative: ExpenseBudgetItemStats["real_commission_relative"];
    planned_commission_relative_computed: ExpenseBudgetItemStats["planned_commission_relative_computed"];
    real_expenses_percent: ExpenseBudgetItemStats["real_expenses_percent"];
    planned_expenses: ExpenseBudgetItemStats["planned_expenses"];
    planned_commission: ExpenseBudgetItemStats["planned_commission"];
    credit: ExpenseBudgetItemStats["credit"];
    credit_balance: ExpenseBudgetItemStats["credit_balance"];
    invoiced_percent: ExpenseBudgetItemStats["invoiced_percent"];
    invoiceable: ExpenseBudgetItemStats["invoiceable"];
    uninvoiced: ExpenseBudgetItemStats["uninvoiced"];
    profit: ExpenseBudgetItemStats["profit"];
    profit_percent: ExpenseBudgetItemStats["profit_percent"];
    client_price: ExpenseBudgetItemStats["client_price"];
    planned_profit: ExpenseBudgetItemStats["planned_profit"];
    actual_profit: number;
  };
  @observable expense_statuses: Array<
    ExpenseBudgetItemExpenseStats["expense"]["status"]
  >;

  constructor(src: TExpenseStructured["budgetItems"][0]) {
    makeObservable(this);
    this.__typename = src.__typename;
    this.id = src.id;
    this.item_code = src.item_code;
    this.title = src.title;
    this.completed_at = src.completed_at ? src.completed_at : undefined;
    this.type = src.type;
    this.hasPlannedExpenses = src.plannedExpenses.length > 0;
    this.requested_for_deletion_at = src.requested_for_deletion_at
      ? src.requested_for_deletion_at
      : undefined;
    this.stats = {
      ...src.stats,
      actual_profit: src.stats.credit - src.stats.real_expenses,
    };
    this.expense_statuses = src.expenseStats.map(
      ({ expense }) => expense.status,
    );
  }

  @action
  updateCompletedAt(value: ExpenseBudgetItem["completed_at"]) {
    this.completed_at = value;
  }

  @action.bound
  updateHasPlannedExpenses(value: boolean) {
    this.hasPlannedExpenses = value;
  }
}

export class ExpenseStructuredModel {
  id: ExpenseStructuredBudgetItem["id"];
  budget_category_id: ExpenseStructuredBudgetItem["budget_category_id"];
  @observable budgetItems: ExpenseBudgetItemModel[];
  name: ExpenseStructuredBudgetItem["name"];
  total_budget: ExpenseStructuredBudgetItem["total_budget"];
  credit: ExpenseStructuredBudgetItem["credit"];

  constructor(src: TExpenseStructured, parentProjectType: ProjectTypeEnum) {
    makeObservable(this);
    this.id = src.id;
    this.budget_category_id = src.budget_category_id;
    this.name = src.name;
    this.total_budget = src.total_budget;
    this.budgetItems = sortBy(
      filter(
        src.budgetItems,
        (bi) =>
          bi.type === ExpenseBudgetItemType.Regular ||
          (bi.type === ExpenseBudgetItemType.Other &&
            (bi.stats.real_expenses !== 0 ||
              parentProjectType === ProjectTypeEnum.TimeMaterials)),
      ),
      [(bi) => bi.type === ExpenseBudgetItemType.Other],
    ).map((i) => new ExpenseBudgetItemModel(i));
    this.credit = src.credit;
  }

  @action.bound addBudgetItem(
    budgetItem: TExpenseStructured["budgetItems"][0],
  ) {
    this.budgetItems.push(new ExpenseBudgetItemModel(budgetItem));
  }

  @computed get hasBudgetItems(): boolean {
    return this.budgetItems.length > 0;
  }
}

export class StatsModel {
  total_budget: ProjectStats["total_budget"];
  credit: ProjectStats["credit"];
  credit_direct: ProjectStats["credit_direct"];
  credit_balance: ProjectStats["credit_balance"];
  credit_from_prepayments: ProjectStats["credit_from_prepayments"];
  invoiceable: ProjectStats["invoiceable"];
  uninvoiced: ProjectStats["uninvoiced"];
  unused_sum_from_proformas: ProjectStats["unused_sum_from_proformas"];
  over_invoiced: ProjectStats["over_invoiced"];
  net_profit: ProjectStats["net_profit"];
  net_margin_percent: ProjectStats["net_margin_percent"];
  gross_income: ProjectStats["gross_income"];

  outgoing_invoice_amount: ProjectStats["outgoing_invoice_amount"];
  outgoing_invoice_paid: ProjectStats["outgoing_invoice_paid"];
  outgoing_proforma_invoice_amount: ProjectStats["outgoing_proforma_invoice_amount"];
  outgoing_proforma_invoice_paid: ProjectStats["outgoing_proforma_invoice_paid"];
  outgoing_proforma_invoice_settled: ProjectStats["outgoing_proforma_invoice_settled"];
  outgoing_proforma_invoice_unsettled: ProjectStats["outgoing_proforma_invoice_unsettled"];
  credit_from_unused_prepayments: ProjectStats["credit_from_unused_prepayments"];

  ourWork: StatsOurWorkModel;
  expenses: StatsExpensesModel;

  constructor(src: TProject["statsCached"]) {
    this.total_budget = src?.total_budget ?? 0;
    this.credit = src?.credit ?? 0;
    this.credit_balance = src?.credit_balance ?? 0;
    this.credit_direct = src?.credit_direct ?? 0;
    this.credit_from_prepayments = src?.credit_from_prepayments ?? 0;
    this.invoiceable = src?.invoiceable ?? 0;
    this.uninvoiced = src?.uninvoiced ?? 0;
    this.unused_sum_from_proformas = src?.unused_sum_from_proformas ?? 0;
    this.over_invoiced = src?.over_invoiced ?? 0;
    this.ourWork = new StatsOurWorkModel(src?.ourWork);
    this.expenses = new StatsExpensesModel(src?.expenses);
    this.net_profit = src?.net_profit ?? 0;
    this.net_margin_percent = src?.net_margin_percent ?? 0;
    this.gross_income = src?.gross_income ?? 0;
    this.outgoing_invoice_amount = src?.outgoing_invoice_amount ?? 0;
    this.outgoing_invoice_paid = src?.outgoing_invoice_paid ?? 0;
    this.outgoing_proforma_invoice_amount =
      src?.outgoing_proforma_invoice_amount ?? 0;
    this.outgoing_proforma_invoice_paid =
      src?.outgoing_proforma_invoice_paid ?? 0;
    this.outgoing_proforma_invoice_settled =
      src?.outgoing_proforma_invoice_settled ?? 0;
    this.outgoing_proforma_invoice_unsettled =
      src?.outgoing_proforma_invoice_unsettled ?? 0;
    this.credit_from_unused_prepayments =
      src?.credit_from_unused_prepayments ?? 0;
  }
}

class StatsOurWorkModel {
  remaining_time: ProjectOurWorkStats["remaining_time"];
  spent_time: ProjectOurWorkStats["spent_time"];
  total_time: ProjectOurWorkStats["total_time"];
  total_budget: ProjectOurWorkStats["total_budget"];
  spent_budget: ProjectOurWorkStats["spent_budget"];
  spent_budget_percent: ProjectOurWorkStats["spent_budget_percent"];
  credit: ProjectOurWorkStats["credit"];
  invoiced_percent: ProjectOurWorkStats["invoiced_percent"];
  invoiceable: ProjectOurWorkStats["invoiceable"];
  uninvoiced: ProjectOurWorkStats["uninvoiced"];
  all_items_completed: ProjectOurWorkStats["all_items_completed"];
  net_profit: ProjectOurWorkStats["net_profit"];
  net_margin_percent: ProjectOurWorkStats["net_margin_percent"];
  remaining_budget: ProjectOurWorkStats["remaining_budget"];
  efficiency_percent: ProjectOurWorkStats["efficiency_percent"];

  constructor(
    src: NonNullable<TProject["statsCached"]>["ourWork"] | undefined,
  ) {
    this.total_time = src?.total_time ?? 0;
    this.remaining_time = src?.remaining_time ?? 0;
    this.spent_time = src?.spent_time ?? 0;
    this.total_budget = src?.total_budget ?? 0;
    this.spent_budget = src?.spent_budget ?? 0;
    this.spent_budget_percent = src?.spent_budget_percent ?? 0;
    this.credit = src?.credit ?? 0;
    this.invoiced_percent = src?.invoiced_percent ?? 0;
    this.invoiceable = src?.invoiceable ?? 0;
    this.uninvoiced = src?.uninvoiced ?? 0;
    this.all_items_completed = src?.all_items_completed ?? false;
    this.net_profit = src?.net_profit ?? 0;
    this.net_margin_percent = src?.net_margin_percent ?? 0;
    this.remaining_budget = src?.remaining_budget ?? 0;
    this.efficiency_percent = src?.efficiency_percent ?? 0;
  }
}

class StatsExpensesModel {
  planned_expenses: ProjectExpenseStats["planned_expenses"];
  planned_commission: ProjectExpenseStats["planned_commission"];
  planned_commission_relative_computed: ProjectExpenseStats["planned_commission_relative_computed"];
  real_expenses: ProjectExpenseStats["real_expenses"];
  real_commission: ProjectExpenseStats["real_commission"];
  total_budget: ProjectExpenseStats["total_budget"];
  real_commission_relative: ProjectExpenseStats["real_commission_relative"];
  credit: ProjectExpenseStats["credit"];
  credit_balance: ProjectExpenseStats["credit_balance"];
  real_expenses_percent: ProjectExpenseStats["real_expenses_percent"];
  invoiced_percent: ProjectExpenseStats["invoiced_percent"];
  invoiceable: ProjectExpenseStats["invoiceable"];
  uninvoiced: ProjectExpenseStats["uninvoiced"];
  all_items_completed: ProjectExpenseStats["all_items_completed"];
  planned_profit: ProjectExpenseStats["planned_profit"];

  constructor(
    src: NonNullable<TProject["statsCached"]>["expenses"] | undefined,
  ) {
    this.planned_expenses = src?.planned_expenses ?? 0;
    this.planned_commission = src?.planned_commission ?? 0;
    this.planned_commission_relative_computed =
      src?.planned_commission_relative_computed ?? "0";
    this.real_expenses = src?.real_expenses ?? 0;
    this.real_commission = src?.real_commission ?? 0;
    this.real_commission_relative = src?.real_commission_relative ?? "0";
    this.total_budget = src?.total_budget ?? 0;
    this.credit = src?.credit ?? 0;
    this.credit_balance = src?.credit_balance ?? 0;
    this.real_expenses_percent = src?.real_expenses_percent ?? 0;
    this.invoiced_percent = src?.invoiced_percent ?? 0;
    this.invoiceable = src?.invoiceable ?? 0;
    this.uninvoiced = src?.uninvoiced ?? 0;
    this.all_items_completed = src?.all_items_completed ?? false;
    this.planned_profit = src?.planned_profit ?? 0;
  }
}

export class ProjectModel {
  id: Project["id"];

  @observable.ref status: Project["status"];

  @observable.ref finished_at: Project["finished_at"];
  @observable.ref finishedBy: TFinishedBy;

  @observable.ref archived_at: Project["archived_at"];
  @observable.ref archivedBy: TArchivedBy;

  @observable.ref orders: Pick<Order, "id" | "purchase_order_no">[];

  title: Project["title"];
  type: Project["type"];
  billable: Project["billable"];
  is_pinned: Project["is_pinned"];
  billingPlanIsMissing: boolean;
  uses_budgetovac?: Project["uses_budgetovac"];
  budgetovacBudget: Project["budgetovacBudget"];
  budgetovacBudgets: TProject["budgetovacBudgets"];
  note: Project["note"];
  code: Project["code"];
  numeric_code: Project["numeric_code"];
  projectManager: TProjectManager;

  userIds: string[];
  otherProjectManagers: ProjectManagerModel[];
  contact: TContact;
  brand: TBrand;
  start_date: Date;
  expected_end_date?: Date;
  team: Pick<Project["team"], "id" | "name">;
  stats: StatsModel;
  cloudUrl: Project["cloudUrl"];
  driveCloudUrl: Project["driveCloudUrl"];
  // ourWorkStructured: OurWorkStructuredModel[];
  @observable ourWorkOverview: OurWorkOverviewModel[] = [];
  expensesStructured: ExpenseStructuredModel[];

  @observable lastWorkInProgressPercent: number;
  invoiceStats: TProjectInvoiceStat;
  proformaInvoiceCount: number;
  prepaymentStats: TProjectPrepaymentStats;
  currentMonthBillingPlanStats: TProject["currentMonthBillingPlanStats"];
  currentMonthBillingPlanOutgoingInvoice: TProject["currentMonthBillingPlanOutgoingInvoice"];
  currentMonthBillingPlanOutgoingProformaInvoice: TProject["currentMonthBillingPlanOutgoingProformaInvoice"];
  categories: TProject["categories"];
  @observable.ref
  billingPlanNotifications: TProject["billingPlanNotifications"];
  @observable.ref externalLinks: ExternalLink[] = [];
  @observable.ref billingPlanSetting: TProject["billingPlanSetting"];
  @observable.ref billingPlanByMonths: TProject["billingPlanByMonths"];
  @observable.ref isTemplate: TProject["is_template"];
  @observable.ref stage: TProject["project_stage"];
  @observable.ref
  pipedriveCustomFieldValues: TProject["pipedriveCustomFieldValues"];
  @observable.ref
  is_starred: TProject["is_starred"];

  constructor(src: TProject) {
    makeObservable(this);
    this.id = src.id;
    this.title = src.title;
    this.type = src.type;
    this.note = src.note;
    this.code = src.code;
    this.numeric_code = src.numeric_code;
    this.status = src.status;
    this.billable = src.billable;
    this.is_pinned = src.is_pinned;
    this.uses_budgetovac = src.uses_budgetovac;
    this.budgetovacBudget = src.budgetovacBudget;
    this.budgetovacBudgets = src.budgetovacBudgets;
    this.finished_at = src.finished_at ? src.finished_at : undefined;
    this.archived_at = src.archived_at ? src.archived_at : undefined;
    this.invoiceStats = src.invoiceStats;
    this.proformaInvoiceCount = src.proformaInvoices.length;
    this.prepaymentStats = src.prepaymentStats;
    this.userIds = [
      src.projectManager.id,
      ...map(src.otherProjectManagers || [], (pm) => pm.id),
    ];
    this.start_date = new Date(src.start_date);
    if (src.expected_end_date) {
      this.expected_end_date = new Date(src.expected_end_date);
    }
    this.team = src.team;
    this.orders = src.orders?.data ?? [];
    this.otherProjectManagers = map(
      src.otherProjectManagers,
      (m) => new ProjectManagerModel(m),
    );
    this.projectManager = src.projectManager;
    this.contact = src.contact;
    this.finishedBy = src.finishedBy;
    this.archivedBy = src.archivedBy;
    this.brand = src.brand;
    this.stats = new StatsModel(src.statsCached);
    this.cloudUrl = src.cloudUrl;
    this.driveCloudUrl = src.driveCloudUrl;
    // this.ourWorkStructured = map(
    //   src.ourWorkStructured,
    //   (w) => new OurWorkStructuredModel(w),
    // );
    // this.ourWorkOverview = map(
    //   src.ourWorkOverview,
    //   (w) => new OurWorkOverviewModel(w),
    // );
    this.expensesStructured = map(
      src.expensesStructured,
      (w) => new ExpenseStructuredModel(w, src.type),
    );
    this.lastWorkInProgressPercent = +(
      src.lastWorkInProgressProjectStat?.progress_percent ?? 0
    );
    this.billingPlanIsMissing = src.billingPlanIsMissing;
    this.currentMonthBillingPlanStats = src.currentMonthBillingPlanStats;
    this.currentMonthBillingPlanOutgoingInvoice =
      src.currentMonthBillingPlanOutgoingInvoice;
    this.currentMonthBillingPlanOutgoingProformaInvoice =
      src.currentMonthBillingPlanOutgoingProformaInvoice;
    this.billingPlanNotifications = src.billingPlanNotifications;
    this.billingPlanSetting = src.billingPlanSetting;
    this.billingPlanByMonths = src.billingPlanByMonths;
    this.categories = src.categories;
    this.isTemplate = src.is_template;
    this.stage = src.project_stage;
    this.pipedriveCustomFieldValues = src.pipedriveCustomFieldValues;

    this.externalLinks = src.externalLinks.map((link) => {
      const href = link.link
        .replaceAll("https://", "")
        .replaceAll("http://", "");

      return {
        ...link,
        link: `https://${href}`,
      };
    });

    this.is_starred = src.is_starred ?? false;

    // if (!this.areOurWorkOverviewStatsDefined(src.ourWorkOverview)) return;
    // this.fetchOurWorkOverview(src.id);
  }

  @computed get expensesStructuredFiltered() {
    return this.expensesStructured.filter(
      ({ hasBudgetItems }) => hasBudgetItems,
    );
  }

  @computed get visiblePipedriveCustomFieldValues() {
    return this.pipedriveCustomFieldValues.filter(
      (item) => !!item.customField.label && !!item.value,
    );
  }

  get invoiceCount() {
    return this.invoiceStats.length;
  }

  get prepaymentCount() {
    return this.prepaymentStats.length;
  }

  get invoiceAndPrepaymentCount() {
    return this.invoiceCount + this.prepaymentCount;
  }

  get hasAnyOurWorkItem() {
    return this.ourWorkOverview.some((i) => i.budgetItems.length);
  }

  get hasAnyExpenseItem() {
    return this.expensesStructured.some((i) => i.budgetItems.length);
  }

  @computed get shouldUseNewInvoicingPlan(): boolean {
    return (
      !!this.billingPlanSetting?.project_id ||
      this.billingPlanByMonths.length === 0
    );
  }

  @computed get isTimeAndMaterials() {
    return this.type === ProjectTypeEnum.TimeMaterials;
  }

  @action.bound
  addOrder(order: ProjectModel["orders"][0]) {
    this.orders = [...this.orders, order];
  }

  @action.bound
  setBillingPlanNotifications(data: ProjectModel["billingPlanNotifications"]) {
    this.billingPlanNotifications = data;
  }

  @action.bound
  setLastWorkInProgressPercent(
    value: ProjectModel["lastWorkInProgressPercent"],
  ) {
    this.lastWorkInProgressPercent = value;
  }

  @action.bound async fetchOurWorkOverview(projectId: string, retryCount = 0) {
    if (retryCount >= 3) return;

    setTimeout(async () => {
      const { data, error } = await client
        .query<
          OurWorkOverviewForProjectQuery,
          OurWorkOverviewForProjectQueryVariables
        >(OurWorkOverviewForProjectDocument, { projectId: projectId })
        .toPromise();

      if (!data?.project || error) return;

      if (this.areOurWorkOverviewStatsDefined(data.project.ourWorkOverview)) {
        this.ourWorkOverview = map(
          data.project.ourWorkOverview,
          (w) => new OurWorkOverviewModel(w),
        );
      } else {
        this.fetchOurWorkOverview(projectId, retryCount + 1);
      }
    }, 3000);
  }

  private areOurWorkOverviewStatsDefined(
    ourWorkOverview: NonNullable<
      OurWorkOverviewForProjectQuery["project"]
    >["ourWorkOverview"],
  ) {
    let everyStatsDefined = true;

    for (const { budgetItems } of ourWorkOverview) {
      for (const { tasks } of budgetItems) {
        for (const { stats } of tasks) {
          if (stats === null) {
            everyStatsDefined = false;
            break;
          }
        }
      }
    }

    return everyStatsDefined;
  }

  @computed
  get hasBudgetsInternallyApproved() {
    return (
      this.budgetovacBudgets
        ?.filter((budget) => budget.is_active)
        .every((budget) => budget.internally_approved) ?? true
    );
  }

  maxVisibleManagersCount = 9;

  @computed
  get projectManagersVisible(): ProjectManagerModel[] {
    if (this.otherProjectManagers.length < this.maxVisibleManagersCount)
      return this.otherProjectManagers;
    return this.otherProjectManagers.slice(0, this.maxVisibleManagersCount - 1);
  }

  @computed
  get projectsManagersExceedingVisibleLimit(): ProjectManagerModel[] {
    if (this.otherProjectManagers.length < 0) return [];
    return this.otherProjectManagers.slice(this.maxVisibleManagersCount - 1);
  }
}

class ProjectManagerModel {
  id: User["id"];
  full_name: User["full_name"];
  profile?: {
    hex_color: UserProfile["hex_color"];
    defaultWorktype: Pick<UserProfile["defaultWorktype"], "id" | "title">;
  } | null;
  image: DeepPick<
    User["image"],
    {
      urls: {
        thumbnail: never;
      };
    }
  >;
  team: Maybe<{
    id: NonNullable<User["team"]>["id"];
    name: NonNullable<User["team"]>["name"];
  }>;

  constructor(src: TOtherProjectManager) {
    this.id = src.id;
    this.profile = src.profile;
    this.image = src.image;
    this.full_name = src.full_name;
    this.team = src.team;
  }
}

export const isOurWorkBudgetItem = (
  arg:
    | OurWorkBudgetItemModel
    | ExpenseBudgetItemModel
    | StatsExpensesModel
    | StatsOurWorkModel,
): arg is OurWorkBudgetItemModel => arg instanceof OurWorkBudgetItemModel;
